import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, ErrorIcon, InfoIcon } from "../icons";
import ArchiveButton from "./ArchiveButton";
import { lookupProject, repairEmailBody, searchProjects } from "../../services/archiveApi";
import { convertToGraphId } from "../../../shared/utils/officeHelpers";

// Detects whether the reader's Outlook is in a dark theme by checking the
// luminance of the host body background. Used so repair forces a readable text
// colour (white on dark, black on light).
function detectOutlookTheme() {
  try {
    const bg = Office.context.officeTheme?.bodyBackgroundColor;
    if (bg) {
      const hex = bg.replace("#", "");
      if (hex.length >= 6) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        return luminance < 128 ? "dark" : "light";
      }
    }
  } catch {
    /* fall through to default */
  }
  return "light";
}

export default function FileTab({
  status,
  apiMessage,
  errorMessage,
  isProcessing,
  onArchive,
  onReset,
}) {
  const [state, setState] = useState({
    projectId: "",
    subjectProjectId: null,
    bodyProjectId: null,
    validationError: null,
    isValid: false,
  });
  const [projectLookup, setProjectLookup] = useState({
    status: "idle",
    projectId: "",
    exactMatch: null,
    matches: [],
    lookupError: null,
  });
  const [repairStatus, setRepairStatus] = useState("idle"); // idle | loading | success | error
  const [repairMessage, setRepairMessage] = useState("");

  // Search-ahead dropdown — lets the user find a project by ID *or* folder name,
  // mirroring the on-send dialog (projectid-dialog.html).
  const [searchResults, setSearchResults] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Skips the next search run after a dropdown selection so picking an item
  // doesn't immediately reopen the dropdown.
  const suppressSearchRef = useRef(false);
  const searchWrapRef = useRef(null);

  const PROJECT_ID_REGEX = /\(?\bCE(?:-P)?\s\d{5}\b\)?/i;
  const PROJECT_ID_INPUT_REGEX = /^CE(?:-P)?\s\d{5}$/i;

  function normalizeProjectIdInput(value) {
    return value ? value.replace(/[()]/g, "") : "";
  }

  function sanitizeProjectId(value) {
    return normalizeProjectIdInput(value).trim();
  }

  function extractProjectId(text) {
    if (!text) {
      return null;
    }

    const match = text.match(PROJECT_ID_REGEX);
    return match ? sanitizeProjectId(match[0].toUpperCase()) : null;
  }

  function readEmailData(item) {
    return new Promise((resolve, reject) => {
      item.body.getAsync("text", (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(result.error);
          return;
        }

        resolve({
          subject: item.subject || "",
          body: result.value || "",
        });
      });
    });
  }

  async function detectProjectId(item) {
    if (!item?.itemId) {
      return;
    }

    const itemIdWhenStarted = item.itemId;

    try {
      const { subject, body } = await readEmailData(item);

      // Ignore late callbacks from a previous message after the user switched items.
      const currentItem = Office.context.mailbox.item;
      if (!currentItem || currentItem.itemId !== itemIdWhenStarted) {
        return;
      }

      const subjectId = extractProjectId(subject);
      const bodyId = extractProjectId(body);

      let finalId = null;
      let error = null;

      if (subjectId && bodyId) {
        if (subjectId === bodyId) {
          finalId = subjectId;
        } else {
          error = `Mismatch: Subject (${subjectId}) vs Body (${bodyId})`;
        }
      } else {
        finalId = subjectId || bodyId;
      }

      setState({
        projectId: finalId || "",
        subjectProjectId: subjectId,
        bodyProjectId: bodyId,
        validationError: error,
        isValid: !!finalId && !error,
      });
    } catch (err) {
      console.error("Detection error:", err);
    }
  }

  useEffect(() => {
    const mailbox = Office.context.mailbox;

    const handler = () => {
      const item = mailbox.item;
      if (item) {
        detectProjectId(item);
      }
    };

    mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler);
    handler();

    return () => {
      mailbox.removeHandlerAsync(Office.EventType.ItemChanged, handler);
    };
  }, []);

  function handleProjectIdChange(value) {
    const normalizedValue = normalizeProjectIdInput(value);
    const normalizedUppercaseValue = normalizedValue.toUpperCase();
    const sanitizedValue = sanitizeProjectId(normalizedUppercaseValue);
    const hasValue = !!sanitizedValue;
    const isValidProjectId = PROJECT_ID_INPUT_REGEX.test(sanitizedValue);

    setState((prev) => ({
      ...prev,
      projectId: normalizedUppercaseValue,
      // No format validation here — the field doubles as a search box (ID or
      // folder name). The Archive button stays gated on a confirmed exact match.
      validationError: null,
      isValid: hasValue && isValidProjectId,
    }));
  }

  const sanitizedProjectId = useMemo(() => sanitizeProjectId(state.projectId), [state.projectId]);
  const canLookupProject = state.isValid && !state.validationError && !!sanitizedProjectId;
  const hasExactProjectMatch = Boolean(projectLookup.exactMatch);

  useEffect(() => {
    if (!canLookupProject) {
      setProjectLookup({
        status: "idle",
        projectId: "",
        exactMatch: null,
        matches: [],
        lookupError: null,
      });
      return;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      setProjectLookup((prev) => ({
        ...prev,
        status: "loading",
        lookupError: null,
      }));

      try {
        const response = await lookupProject(sanitizedProjectId);

        if (isCancelled) {
          return;
        }

        setProjectLookup({
          status: response.exactMatch ? "success" : "not_found",
          projectId: response.projectId || sanitizedProjectId,
          exactMatch: response.exactMatch || null,
          matches: response.matches || [],
          lookupError: null,
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error("Project lookup failed:", error);

        setProjectLookup({
          status: "error",
          projectId: sanitizedProjectId,
          exactMatch: null,
          matches: [],
          lookupError: error?.message || "Request failed.",
        });
      }
    }, 250);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [canLookupProject, sanitizedProjectId]);

  // Search-ahead: query by project ID or folder name as the user types and
  // surface matches in the dropdown (250 ms debounce, same as the on-send dialog).
  useEffect(() => {
    if (suppressSearchRef.current) {
      suppressSearchRef.current = false;
      return;
    }

    const query = sanitizeProjectId(state.projectId);
    if (query.length < 2) {
      setSearchResults([]);
      setDropdownOpen(false);
      return;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const response = await searchProjects(query);
        if (isCancelled) {
          return;
        }
        const matches = response.matches || [];
        setSearchResults(matches);
        setDropdownOpen(matches.length > 0);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        console.error("Project search failed:", error);
        setSearchResults([]);
        setDropdownOpen(false);
      }
    }, 250);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [state.projectId]);

  // Close the dropdown when clicking outside the search input/list.
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Projects are stored as bare 5-digit numbers with no CE/CE-P prefix — the
  // public-vs-private choice comes entirely from what the user typed. Preserve
  // it so a "CE-P" search stays private (CE-P) instead of defaulting to CE.
  function prefixedId(bareId) {
    const wantsPrivate = /^\s*CE-P/i.test(state.projectId);
    return /^\d{5}$/.test(String(bareId))
      ? `${wantsPrivate ? "CE-P" : "CE"} ${bareId}`
      : bareId;
  }

  function handleSelectProject(project) {
    const formatted = prefixedId(project.ProjectId);
    suppressSearchRef.current = true;
    setDropdownOpen(false);
    setSearchResults([]);
    handleProjectIdChange(formatted);
  }

  function handleArchiveClick() {
    onArchive(sanitizedProjectId);
  }

  async function handleRepairClick() {
    const item = Office.context.mailbox.item;
    const ewsId = item?.itemId;
    const itemId = ewsId ? convertToGraphId(ewsId) : null;
    const internetMessageId = item?.internetMessageId || null;
    const userEmail = Office.context.mailbox.userProfile?.emailAddress;
    if ((!itemId && !internetMessageId) || !userEmail) {
      setRepairStatus("error");
      setRepairMessage("Could not read current email or user email.");
      return;
    }
    setRepairStatus("loading");
    setRepairMessage("");
    try {
      const theme = detectOutlookTheme();
      const result = await repairEmailBody({ emailId: itemId, userEmail, internetMessageId, theme });
      setRepairStatus(result.success === false ? "error" : "success");
      setRepairMessage(result.message || "Email body repaired successfully.");
    } catch (err) {
      setRepairStatus("error");
      setRepairMessage(err.message || "Repair failed. The email may not be filed yet.");
    }
  }

  // const testOpenDialogClick = () => {
  //   const itemId = Office.context.mailbox.item?.itemId;
  //   if (!itemId) {
  //     console.error("No item ID available to open.");
  //     return;
  //   }
  //   Office.context.mailbox.displayMessageForm(itemId, function (asyncResult) {
  //     if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
  //       console.error("Failed to open the message: " + asyncResult.error.message);
  //     }
  //   });
  // };

  return (
    <>
      <div className="card card-allow-overflow">
        {/* <button onClick={testOpenDialogClick}>Open Message</button> */}
        <div className="card-header">
          <div className="card-header-dot"></div>
          <h3>Archive this email</h3>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ fontSize: 12, color: "var(--ms-neutral-60)", lineHeight: 1.6 }}>
            Saves this message and all its attachments securely to Azure Blob Storage and logs
            metadata to SQL.
          </p>

          <div className="project-search" ref={searchWrapRef}>
            <input
              type="text"
              value={state.projectId}
              onChange={(e) => handleProjectIdChange(e.target.value)}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setDropdownOpen(true);
                }
              }}
              placeholder="Search by project ID or name"
              className="form-input"
              autoComplete="off"
            />
            {dropdownOpen && searchResults.length > 0 && (
              <ul className="project-search-dropdown">
                {searchResults.map((project) => {
                  const displayId = prefixedId(project.ProjectId);
                  return (
                    <li
                      key={project.ProjectId}
                      onMouseDown={(e) => {
                        // Prevent the input from losing focus before selection.
                        e.preventDefault();
                        handleSelectProject(project);
                      }}
                    >
                      <span className="dd-id">{displayId}</span>
                      {project.ProjectFolderName && (
                        <span className="dd-name">{project.ProjectFolderName}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {status !== "success" && (
            <ArchiveButton
              isProcessing={isProcessing}
              onClick={handleArchiveClick}
              disabled={
                !state.isValid || projectLookup.status === "loading" || !hasExactProjectMatch
              }
            />
          )}

          {projectLookup.status === "loading" && (
            <div className="status-banner info">
              <InfoIcon />
              <div>Checking project folder for {sanitizedProjectId}...</div>
            </div>
          )}

          {projectLookup.status === "success" && projectLookup.exactMatch && (
            <div className="status-banner info">
              <InfoIcon />
              <div>
                <strong>Archive destination:</strong> {projectLookup.exactMatch.ProjectFolderName}
                <br />
                <strong>Matched project:</strong> {prefixedId(projectLookup.exactMatch.ProjectId)}
              </div>
            </div>
          )}

          {projectLookup.status === "not_found" && (
            <div className="status-banner error">
              <ErrorIcon />
              <div>
                <strong>Project ID not matched.</strong> No saved project folder was found for{" "}
                {projectLookup.projectId || sanitizedProjectId}.
              </div>
            </div>
          )}

          {projectLookup.status === "error" && (
            <div className="status-banner error">
              <ErrorIcon />
              <div>
                <strong>Project lookup failed.</strong>{" "}
                {projectLookup.lookupError || "We could not verify the archive folder right now."}
              </div>
            </div>
          )}

          {state.validationError && !dropdownOpen && (
            <div className="status-banner error">
              <ErrorIcon />
              <div>{state.validationError}</div>
            </div>
          )}

          {status === "success" && (
            <>
              <div className="status-banner success">
                <CheckIcon />
                <div>
                  <strong>Filed successfully.</strong>{" "}
                  {apiMessage || "Message is stored in Azure Blob Storage and indexed in SQL."}
                </div>
              </div>
              <button
                className="btn-primary"
                onClick={onReset}
                type="button"
                style={{ background: "var(--ms-neutral-60)", borderColor: "var(--ms-neutral-60)" }}
              >
                Archive another email
              </button>
            </>
          )}

          {status === "error" && (
            <div className="status-banner error">
              <ErrorIcon />
              <div>
                <strong>Filing failed.</strong>{" "}
                {errorMessage || "Check your network connection and try again."}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-header-dot"></div>
          <h3>Repair email body - Beta</h3>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ fontSize: 12, color: "var(--ms-neutral-60)", lineHeight: 1.6 }}>
            If this email was filed but the body is hard to read — text that's invisible against
            your theme, or far too small — use this to restore it to a readable colour and size.
          </p>
          {repairStatus !== "success" && (
            <button
              className="btn-primary"
              onClick={handleRepairClick}
              disabled={repairStatus === "loading"}
              type="button"
            >
              {repairStatus === "loading" ? "Repairing…" : "Repair Email Body"}
            </button>
          )}
          {repairStatus === "success" && (
            <>
              <div className="status-banner success">
                <CheckIcon />
                <div>{repairMessage}</div>
              </div>
              <button
                className="btn-primary"
                onClick={() => { setRepairStatus("idle"); setRepairMessage(""); }}
                type="button"
                style={{ background: "var(--ms-neutral-60)", borderColor: "var(--ms-neutral-60)" }}
              >
                Repair another
              </button>
            </>
          )}
          {repairStatus === "error" && (
            <div className="status-banner error">
              <ErrorIcon />
              <div>{repairMessage}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
